/**
 * ReportSources — "Tietopohja" section.
 * Two collapsible tables: perustietopohja (global datasets) +
 * Kuutio-spesifi tietopohja (instance-assigned datasets).
 *
 * Layout (2026-06): # / Nimi (hyperlinked) / Julkaisija. The Linkki
 * column is gone — the title itself opens the source in a new tab when
 * a URL exists. Admins edit the URL via the title's edit modal, which
 * shows a Linkki input below the title text.
 *
 * Data source: API_ENDPOINTS.RAG_SOURCES(instanceId) (RAG sourcesRouter).
 *
 * Admin overlay (report_edits):
 *   source.<docId>.title       (override)
 *   source.<docId>.publisher   (override)
 *   source.<docId>.url         (override — surfaced in title edit modal)
 *   source.<docId>.hidden      "1" → row hidden from public view
 *   source.custom.<group>.count        number of admin-added rows in this group
 *   source.custom.<group>.<i>.title    custom row title (i = 0…count-1)
 *   source.custom.<group>.<i>.publisher
 *   source.custom.<group>.<i>.url
 *
 * Discrepancy badges visible in edit mode flag:
 *   Muokattu  — any field edited on a DB-sourced row
 *   Piilotettu — row hidden via overlay
 *   Lisätty käsin — row exists only in overlay, not in the source DB
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronUp, Loader2, EyeOff, Eye, Plus, Check, AlertCircle } from 'lucide-react';
import { Section, SectionTitle, Paragraph, SITRA } from './reportStyles';
import { API_ENDPOINTS } from '../../config/api';
import EditableText from './EditableText';
import { useReportEdits } from './ReportEditsContext';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Accordion = styled.div`
  border: 1px solid ${SITRA.border};
  margin-top: 16px;
  background: ${SITRA.white};
`;

const AccordionHeader = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: transparent;
  border: none;
  border-bottom: ${p => p.$open ? `1px solid ${SITRA.border}` : 'none'};
  cursor: pointer;
  font: inherit;
  font-size: 1rem;
  font-weight: 700;
  color: ${SITRA.black};
  text-align: left;
  &:hover { background: ${SITRA.surfaceMuted}; }
`;

const AccordionBody = styled.div`
  padding: 8px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 20px;
  background: ${SITRA.surfaceMuted};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${SITRA.textMuted};
  border-bottom: 1px solid ${SITRA.border};
`;

const Td = styled.td`
  padding: 12px 20px;
  border-bottom: 1px solid ${SITRA.border};
  color: ${SITRA.textBody};
  vertical-align: top;
  &:first-of-type { color: ${SITRA.textMuted}; font-variant-numeric: tabular-nums; width: 40px; }
  ${p => p.$hidden && 'opacity: 0.55; text-decoration: line-through;'}
`;

const Loading = styled.div`
  padding: 32px;
  text-align: center;
  color: ${SITRA.textMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const Empty = styled.div`
  padding: 24px;
  color: ${SITRA.textMuted};
  font-style: italic;
  font-size: 0.9rem;
`;

const ErrorBox = styled.div`
  padding: 16px 20px;
  color: #b91c1c;
  background: #fef2f2;
  font-size: 0.85rem;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
  background: ${p => p.$bg || '#f5f5f5'};
  color: ${p => p.$color || '#374151'};
`;

const IconBtn = styled.button`
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 2px 6px;
  cursor: pointer;
  color: ${p => p.$active ? '#b91c1c' : '#6b7280'};
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  &:hover { background: #f3f4f6; }
`;

const AddRow = styled.div`
  padding: 12px 20px;
  display: flex;
  justify-content: flex-end;
`;

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${SITRA.blue};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

function discrepancyBadges({ edited, hidden, isCustom, t }) {
  return (
    <>
      {hidden && (
        <Badge $bg="#fee2e2" $color="#b91c1c"><EyeOff size={10} /> {t('report.sources.hiddenBadge')}</Badge>
      )}
      {edited && !isCustom && (
        <Badge $bg="#fef3c7" $color="#92400e"><Check size={10} /> {t('report.sources.editedBadge')}</Badge>
      )}
      {isCustom && (
        <Badge $bg="#dbeafe" $color="#1e40af"><Plus size={10} /> {t('report.sources.addedBadge')}</Badge>
      )}
    </>
  );
}

function SourceRow({ doc, index, group }) {
  const { get, isEdited, save, editMode, canEdit, savingKeys } = useReportEdits();
  const { t } = useTranslation();
  const hiddenKey = `source.${doc.id}.hidden`;
  const urlKey = `source.${doc.id}.url`;
  const isHidden = (get(hiddenKey, '') || '').trim() === '1';
  const linkUrl = get(urlKey, doc.url || '');
  const anyEdit = ['title', 'publisher', 'url'].some(f => isEdited(`source.${doc.id}.${f}`));
  const savingHide = savingKeys.has(hiddenKey);

  // Public view: hidden rows are entirely dropped from the render so
  // there's nothing to see. Admins in edit mode still see them so they
  // can flip them back.
  if (isHidden && !(canEdit && editMode)) return null;

  return (
    <tr>
      <Td $hidden={isHidden}>{index + 1}</Td>
      <Td $hidden={isHidden}>
        <EditableText
          fieldKey={`source.${doc.id}.title`}
          fallback={doc.title}
          label={`${doc.title} — nimi`}
          link={linkUrl}
          linkFieldKey={urlKey}
          linkFallback={doc.url || ''}
          linkLabel="Linkki"
          multiline={false}
        />
        {canEdit && editMode && discrepancyBadges({ edited: anyEdit, hidden: isHidden, isCustom: false, t })}
      </Td>
      <Td $hidden={isHidden}>
        <EditableText
          fieldKey={`source.${doc.id}.publisher`}
          fallback={doc.publisher || '—'}
          label={`${doc.title} — julkaisija`}
          multiline={false}
        />
        {canEdit && editMode && (
          <IconBtn
            type="button"
            $active={isHidden}
            disabled={savingHide}
            onClick={() => save(hiddenKey, isHidden ? '' : '1')}
            title={isHidden ? t('report.sources.restoreTitle') : t('report.sources.hideTitle')}
          >
            {savingHide
              ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
              : isHidden ? <Eye size={12} /> : <EyeOff size={12} />}
            {isHidden ? t('report.sources.show') : t('report.sources.hide')}
          </IconBtn>
        )}
      </Td>
    </tr>
  );
}

function CustomSourceRow({ group, idx }) {
  const { get, canEdit, editMode } = useReportEdits();
  const { t } = useTranslation();
  const title = (get(`source.custom.${group}.${idx}.title`, '') || '').trim();
  const url = get(`source.custom.${group}.${idx}.url`, '');
  // In view mode hide custom rows that have no title — they're just
  // empty slots an admin added but didn't fill in.
  if (!title && !(canEdit && editMode)) return null;

  return (
    <tr>
      <Td>+</Td>
      <Td>
        <EditableText
          fieldKey={`source.custom.${group}.${idx}.title`}
          fallback={t('report.sources.manualSource')}
          label={`Lisätty lähde ${idx + 1} — nimi`}
          link={url}
          linkFieldKey={`source.custom.${group}.${idx}.url`}
          linkFallback=""
          linkLabel="Linkki"
          multiline={false}
        />
        {canEdit && editMode && discrepancyBadges({ edited: false, hidden: false, isCustom: true, t })}
      </Td>
      <Td>
        <EditableText
          fieldKey={`source.custom.${group}.${idx}.publisher`}
          fallback="—"
          label={`Lisätty lähde ${idx + 1} — julkaisija`}
          multiline={false}
        />
      </Td>
    </tr>
  );
}

function SourcesTable({ rows, group }) {
  const { get, save, canEdit, editMode, savingKeys } = useReportEdits();
  const { t } = useTranslation();
  const countKey = `source.custom.${group}.count`;
  const customCount = parseInt(get(countKey, '0'), 10) || 0;
  const savingCount = savingKeys.has(countKey);

  if (!rows.length && customCount === 0 && !(canEdit && editMode)) {
    return <Empty>{t('report.sources.empty')}</Empty>;
  }

  return (
    <>
      <Table>
        <thead>
          <tr>
            <Th>#</Th>
            <Th>{t('report.sources.colName')}</Th>
            <Th>{t('report.sources.colPublisher')}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((doc, i) => (
            <SourceRow key={doc.id} doc={doc} index={i} group={group} />
          ))}
          {Array.from({ length: customCount }).map((_, i) => (
            <CustomSourceRow key={`custom-${i}`} group={group} idx={i} />
          ))}
        </tbody>
      </Table>
      {canEdit && editMode && (
        <AddRow>
          <AddBtn
            type="button"
            disabled={savingCount}
            onClick={() => save(countKey, String(customCount + 1))}
            title={t('report.sources.addSourceTitle')}
          >
            {savingCount
              ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              : <Plus size={14} />}
            {t('report.sources.addSource')}
          </AddBtn>
        </AddRow>
      )}
    </>
  );
}

export default function ReportSources({ instanceId, instanceTopic }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openBase, setOpenBase] = useState(false);
  const [openInstance, setOpenInstance] = useState(false);
  const { canEdit, editMode } = useReportEdits();

  useEffect(() => {
    if (!instanceId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    fetch(API_ENDPOINTS.RAG_SOURCES(instanceId))
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(json => { if (!cancelled) { setData(json); setError(null); } })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [instanceId]);

  const baseRows = data?.base || [];
  const instanceRows = data?.instance || [];
  const total = baseRows.length + instanceRows.length;
  const topicLabel = instanceTopic ? `${instanceTopic} -tietopohja` : t('report.sources.instanceTopicFallback');

  return (
    <Section id="sources">
      <SectionTitle>{t('report.sources.title')}</SectionTitle>
      <Paragraph>
        {t('report.sources.intro1')}
      </Paragraph>
      <Paragraph>
        {t('report.sources.intro2')}
      </Paragraph>
      {!loading && !error && (
        <Paragraph>
          {t('report.sources.totalUsed', { count: total })}
        </Paragraph>
      )}

      {canEdit && editMode && (
        <Paragraph style={{
          background: '#fef3c7', borderLeft: '4px solid #f59e0b',
          padding: '10px 14px', fontSize: '0.85rem', color: '#92400e',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertCircle size={16} style={{ flexShrink: 0 }} />
          {t('report.sources.adminOverlayNote')}
        </Paragraph>
      )}

      {loading && (
        <Loading>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          {t('report.sources.loading')}
        </Loading>
      )}

      {error && (
        <ErrorBox>{t('report.sources.loadError', { error })}</ErrorBox>
      )}

      {!loading && !error && (
        <>
          <Accordion>
            <AccordionHeader $open={openBase} onClick={() => setOpenBase(o => !o)} aria-expanded={openBase}>
              <span>{t('report.sources.baseAccordion', { count: baseRows.length })}</span>
              {openBase ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </AccordionHeader>
            {openBase && <AccordionBody><SourcesTable rows={baseRows} group="base" /></AccordionBody>}
          </Accordion>

          <Accordion>
            <AccordionHeader $open={openInstance} onClick={() => setOpenInstance(o => !o)} aria-expanded={openInstance}>
              <span>{t('report.sources.instanceAccordion', { topic: topicLabel, count: instanceRows.length })}</span>
              {openInstance ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </AccordionHeader>
            {openInstance && <AccordionBody><SourcesTable rows={instanceRows} group="instance" /></AccordionBody>}
          </Accordion>
        </>
      )}
    </Section>
  );
}
